import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Row, Col, Form, Button, Table, Badge, Spinner,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserTag, faCircleInfo, faTriangleExclamation,
  faCheckCircle, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { fetchCaseManagerDropdown, addCaseManagerCustomers } from '../services/api';
import CaseManagerAssignmentGuide from './CaseManagerAssignmentGuide/CaseManagerAssignmentGuide';

function PatientReassignmentModal({ show, onHide, patients = [] }) {
  const [caseManagers, setCaseManagers] = useState([]);
  const [loadingCM, setLoadingCM] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [selectedCaseManagerId, setSelectedCaseManagerId] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});

  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);

  // Derive clientId from the first selected patient (all share the same client)
  const clientId = patients[0]?.entityMasterId ?? null;

  const getCaseManagerLabel = (cm) =>
    `${cm?.firstName ?? ''} ${cm?.lastName ?? ''}`.trim() || cm?.name || '';

  // Exclude currently assigned case managers — only show others for reassignment
  const availableCaseManagers = useMemo(() => {
    const assignedIds = new Set();
    const assignedNames = new Set();

    patients.forEach((p) => {
      if (p?.assignedCaseManagerId != null && p.assignedCaseManagerId !== '') {
        assignedIds.add(String(p.assignedCaseManagerId));
      }
      const name = String(p?.assignedCaseManagerName || '').trim().toLowerCase();
      if (name) assignedNames.add(name);
    });

    // Resolve assigned IDs from dropdown names when patient rows only have the name
    caseManagers.forEach((cm) => {
      const label = getCaseManagerLabel(cm).toLowerCase();
      if (label && assignedNames.has(label)) {
        assignedIds.add(String(cm.id));
      }
    });

    return caseManagers.filter((cm) => {
      if (assignedIds.has(String(cm.id))) return false;
      const label = getCaseManagerLabel(cm).toLowerCase();
      if (label && assignedNames.has(label)) return false;
      return true;
    });
  }, [caseManagers, patients]);

  // Fetch case manager dropdown whenever the modal opens
  useEffect(() => {
    if (!show) return;

    if (!clientId) {
      setLoadingCM(false);
      setLoadError('');
      setCaseManagers([]);
      setSelectedCaseManagerId('');
      return;
    }

    let cancelled = false;
    setLoadingCM(true);
    setLoadError('');
    setCaseManagers([]);
    setSelectedCaseManagerId('');

    fetchCaseManagerDropdown(clientId)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data ?? {};
        const items = Array.isArray(payload)
          ? payload
          : (payload.items ?? payload.caseManagers ?? []);
        setCaseManagers(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.message || err?.message || 'Failed to load case managers.',
        );
      })
      .finally(() => { if (!cancelled) setLoadingCM(false); });

    return () => { cancelled = true; };
  }, [show, clientId]);

  const reset = () => {
    setSelectedCaseManagerId('');
    setReason('');
    setErrors({});
    setShowConfirm(false);
    setIsSaving(false);
    setSaveError('');
    setShowSuccess(false);
    setCaseManagers([]);
    setLoadError('');
    setLoadingCM(false);
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const validate = () => {
    const next = {};
    if (!selectedCaseManagerId) next.caseManager = 'Please select a Case Manager / CRE.';
    return next;
  };

  const handleSaveContinue = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaveError('');
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      const customerIds = patients.map((p) => p.id).filter(Boolean);
      await addCaseManagerCustomers(selectedCaseManagerId, { customerIds });
      setShowConfirm(false);
      setShowSuccess(true);
    } catch (err) {
      setSaveError(
        err?.response?.data?.message || err?.message || 'Failed to reassign. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const selectedCM = availableCaseManagers.find(
    (cm) => String(cm.id) === String(selectedCaseManagerId),
  );
  const selectedCMName = selectedCM
    ? `${selectedCM.firstName ?? ''} ${selectedCM.lastName ?? ''}`.trim() || selectedCM.name || String(selectedCM.id)
    : '';

  return (
    <>
      {/* ── Main Modal ── */}
      <Modal
        show={show && !showConfirm && !showSuccess && !showUserGuide}
        onHide={handleClose}
        size="xl"
        centered
        backdrop="static"
        className="patient-reassignment-modal"
      >
        {/* Header */}
        <Modal.Header
          className="border-0 pb-0"
          style={{ background: 'linear-gradient(135deg, #1a56db 0%, #0b3d91 100%)', borderRadius: '0.5rem 0.5rem 0 0' }}
        >
          <div className="d-flex align-items-center gap-3 text-white">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ width: 42, height: 42, backgroundColor: 'rgba(255,255,255,0.15)' }}
            >
              <FontAwesomeIcon icon={faUserTag} style={{ fontSize: '1.1rem' }} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 fw-bold" style={{ letterSpacing: '0.03em' }}>Reassign Case Manager / CRE</h5>
                <Button
                  variant="link"
                  onClick={() => setShowUserGuide(true)}
                  className="text-white p-0"
                  style={{ fontSize: '1rem', opacity: 0.95, lineHeight: 1 }}
                  aria-label="Open Case Manager assignment user guide"
                  title="User guide"
                >
                  <FontAwesomeIcon icon={faCircleInfo} />
                </Button>
              </div>
              <p className="mb-0 opacity-75" style={{ fontSize: '0.78rem' }}>
                Review and assign a new Case Manager / CRE for the selected device users
              </p>
            </div>
          </div>
          <Button
            variant="link"
            onClick={handleClose}
            className="ms-auto text-white p-1"
            style={{ fontSize: '1.1rem', opacity: 0.8 }}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </Modal.Header>

        <Modal.Body className="pt-4 px-4 pb-2">
          <Row className="g-4">
            {/* ── Left: form ── */}
            <Col lg={8}>
              {/* Patient table */}
              <div className="mb-4">
                <p className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: '0.72rem', letterSpacing: '0.08em' }}>
                  Device users selected for reassignment
                </p>
                <div className="rounded border overflow-hidden">
                  <Table className="mb-0 align-middle" size="sm" hover>
                    <thead style={{ backgroundColor: '#f0f4ff' }}>
                      <tr>
                        <th className="text-muted fw-semibold ps-3" style={{ fontSize: '0.75rem', width: 48 }}>#</th>
                        <th className="text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>ID</th>
                        <th className="text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>Entity / Client</th>
                        <th className="text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>Current Case Manager / CRE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-3" style={{ fontSize: '0.85rem' }}>
                            No patients selected.
                          </td>
                        </tr>
                      ) : (
                        patients.map((p, idx) => (
                          <tr key={p.id || idx}>
                            <td className="ps-3 text-muted" style={{ fontSize: '0.85rem' }}>{idx + 1}</td>
                            <td>
                              <span className="fw-semibold text-primary" style={{ fontSize: '0.85rem' }}>
                                {p.recordId || p.id}
                              </span>
                            </td>
                            <td style={{ fontSize: '0.85rem' }}>{p.clientName || '—'}</td>
                            <td>
                              {p.assignedCaseManagerName ? (
                                <Badge bg="light" text="dark" className="border fw-normal" style={{ fontSize: '0.78rem' }}>
                                  {p.assignedCaseManagerName}
                                </Badge>
                              ) : (
                                <span className="text-muted" style={{ fontSize: '0.82rem' }}>Not assigned</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* New Case Manager */}
              {loadingCM && (
                <div className="d-flex align-items-center gap-2 text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                  <Spinner animation="border" size="sm" /> Loading case managers…
                </div>
              )}

              {!loadingCM && loadError && (
                <div className="text-danger mb-3" style={{ fontSize: '0.83rem' }}>{loadError}</div>
              )}

              {!loadingCM && !loadError && availableCaseManagers.length === 0 && (
                <div
                  className="rounded-2 p-3 mb-3"
                  style={{ background: '#fff8e6', border: '1px solid #ffe082' }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="mt-1"
                      style={{ color: '#e67e22', fontSize: '0.9rem', flexShrink: 0 }}
                    />
                    <p className="mb-0 text-dark" style={{ fontSize: '0.88rem' }}>
                      No case manager available for reassignment
                    </p>
                  </div>
                </div>
              )}

              {!loadingCM && !loadError && availableCaseManagers.length > 0 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                      New Case Manager / CRE <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedCaseManagerId}
                      onChange={(e) => {
                        setSelectedCaseManagerId(e.target.value);
                        if (e.target.value) setErrors((prev) => { const { caseManager: _, ...rest } = prev; return rest; });
                      }}
                      isInvalid={!!errors.caseManager}
                      style={{ fontSize: '0.88rem' }}
                    >
                      <option value="">Select Case Manager / CRE…</option>
                      {availableCaseManagers.map((cm) => {
                        const label = getCaseManagerLabel(cm) || String(cm.id);
                        return <option key={cm.id} value={cm.id}>{label}</option>;
                      })}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.caseManager}</Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="primary"
                    className="px-4 py-2 fw-semibold"
                    style={{ letterSpacing: '0.03em', fontSize: '0.88rem' }}
                    onClick={handleSaveContinue}
                  >
                    <FontAwesomeIcon icon={faUserTag} className="me-2" />
                    Save &amp; Continue
                  </Button>
                </>
              )}
            </Col>

            {/* ── Right: info card ── */}
            <Col lg={4}>
              <div
                className="rounded-3 h-100 p-4 d-flex flex-column gap-3"
                style={{ background: '#f8faff', border: '1px solid #dce8ff' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#1a56db', fontSize: '1.1rem' }} />
                  <span className="fw-semibold text-primary" style={{ fontSize: '0.85rem' }}>About Device User Reassignment</span>
                </div>
                <p className="text-muted mb-0" style={{ fontSize: '0.82rem', lineHeight: 1.65 }}>
                  You have the ability to reassign a Case Manager / CRE (permanent assignment) for a selected group of device users to a different person.
                </p>
                <p className="text-muted mb-0" style={{ fontSize: '0.82rem', lineHeight: 1.65 }}>
                  This feature can be used when the primary Case Manager / CRE has left the organisation or moved on to another position.
                </p>
                <p className="text-muted mb-0" style={{ fontSize: '0.82rem', lineHeight: 1.65 }}>
                  Before you can complete this change you must indicate a reason for the reassignment. Once you click <strong>Save &amp; Continue</strong>, a confirmation step will allow you to cancel if this was done in error.
                </p>
                <div
                  className="rounded-2 p-3 mt-auto"
                  style={{ background: '#fff3cd', border: '1px solid #ffe082' }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mt-1" style={{ color: '#e67e22', fontSize: '0.85rem', flexShrink: 0 }} />
                    <p className="mb-0 text-dark" style={{ fontSize: '0.78rem', lineHeight: 1.55 }}>
                      Only device users belonging to the <strong>same client</strong> can be reassigned together.
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0" />
      </Modal>

      {/* ── Confirmation Modal ── */}
      <Modal show={showConfirm} onHide={() => !isSaving && setShowConfirm(false)} centered size="md">
        <Modal.Body className="text-center px-4 pt-4 pb-3">
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
            style={{ width: 64, height: 64, backgroundColor: '#fff3cd' }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '1.75rem', color: '#e67e22' }} />
          </div>
          <h5 className="fw-semibold mb-1">Confirm Reassignment</h5>
          <p className="text-muted mb-0" style={{ fontSize: '0.88rem', lineHeight: 1.6 }}>
            You are about to reassign <strong>{patients.length} device user{patients.length !== 1 ? 's' : ''}</strong> to{' '}
            <strong>{selectedCMName}</strong>. This action cannot be undone easily.
          </p>
          {reason && (
            <p className="mt-2 text-muted mb-0" style={{ fontSize: '0.82rem' }}>
              Reason: <em>{reason}</em>
            </p>
          )}
          {saveError && (
            <p className="mt-2 text-danger mb-0" style={{ fontSize: '0.83rem' }}>{saveError}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0 pt-0 pb-3 gap-2">
          <Button
            variant="light"
            className="border px-4"
            onClick={() => setShowConfirm(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button variant="primary" className="px-4" onClick={handleConfirm} disabled={isSaving}>
            {isSaving ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</> : 'Confirm Reassign'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Success Modal ── */}
      <Modal show={showSuccess} onHide={handleClose} centered>
        <Modal.Body className="text-center px-4 pt-4 pb-3">
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
            style={{ width: 64, height: 64, backgroundColor: '#d1fae5' }}
          >
            <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '1.75rem', color: '#10b981' }} />
          </div>
          <h5 className="fw-semibold mb-2">Reassignment Successful</h5>
          <p className="text-muted mb-0" style={{ fontSize: '0.88rem', lineHeight: 1.6 }}>
            {patients.length} device user{patients.length !== 1 ? 's have' : ' has'} been successfully reassigned to{' '}
            <strong>{selectedCMName}</strong>.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0 pt-0 pb-3">
          <Button variant="success" className="px-4" onClick={handleClose}>Done</Button>
        </Modal.Footer>
      </Modal>

      <CaseManagerAssignmentGuide
        show={showUserGuide}
        onHide={() => setShowUserGuide(false)}
      />
    </>
  );
}

export default PatientReassignmentModal;
